/* eslint-disable react/require-default-props */
/* eslint-disable react/forbid-prop-types */
import React, { useState, useRef, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import SectionHeader from './SectionHeader';
import DuplicateTable from "./DuplicateReport/DuplicateTable";
import { downloadPDF } from '../../utils/downloadPDF';

const noop = () => {};

const PAGE_SIZE = 5;

const DuplicateSection = ({
  title,
  columns,
  data,
  onTagPrimary,
  onMarkDuplicate,
  selectedRows,
  onRowSelect,
  actionLabel,
  showActions,
  emptyComponent,
  showCheckbox = true,
  tableClassName,
}) => {
  const [internalSelected, setInternalSelected] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const tableRef = useRef(null);
  const exportRef = useRef(null);
  const pendingPageAfterActionRef = useRef(null);

  const effectiveSelected = selectedRows ?? internalSelected;
  const effectiveSetSelected = onRowSelect ?? setInternalSelected;

  const totalPages = Math.ceil(data.length / PAGE_SIZE);
  const pagedData = useMemo(
    () => data.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE),
    [data, currentPage],
  );
  const displayTitle = data.length > 0 ? `${title} - ${data.length}` : title;

  useEffect(() => {
    const safeTotalPages = Math.max(totalPages, 1);

    if (pendingPageAfterActionRef.current !== null) {
      const requestedPage = pendingPageAfterActionRef.current;
      pendingPageAfterActionRef.current = null;
      const clampedRequestedPage = Math.min(Math.max(requestedPage, 1), safeTotalPages);
      if (currentPage !== clampedRequestedPage) {
        setCurrentPage(clampedRequestedPage);
        return;
      }
    }

    if (currentPage > safeTotalPages) {
      setCurrentPage(safeTotalPages);
      effectiveSetSelected([]);
    }
  }, [currentPage, totalPages, effectiveSetSelected]);

  const handlePageChange = (nextPage) => {
    setCurrentPage(nextPage);
    effectiveSetSelected([]);
  };

  // Trigger PDF export: mounts an off-screen copy of the table with the full
  // dataset, then captures it (see the effect below). This ensures every record
  // across all pages is exported, not just the currently visible page.
  const handleDownloadPDF = () => setIsExporting(true);

  useEffect(() => {
    if (!isExporting) return undefined;

    let cancelled = false;

    const captureFullTable = async () => {
      // Wait two frames so the off-screen full-data table has laid out.
      await new Promise((resolve) =>
        requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (cancelled) return;

      const exportEl = exportRef.current;
      // Match the on-screen width so the exported layout looks the same.
      if (exportEl && tableRef.current) {
        exportEl.style.width = `${tableRef.current.offsetWidth}px`;
      }

      try {
        await downloadPDF(exportEl, title);
      } finally {
        if (!cancelled) setIsExporting(false);
      }
    };

    captureFullTable();

    return () => {
      cancelled = true;
    };
  }, [isExporting, title]);

  return (
    <>
      <SectionHeader
        title={title}
        displayTitle={displayTitle}
        hasSelection={effectiveSelected.length > 0}
        showActions={showActions}
        actionLabel={actionLabel}
        onAction={() => {
          const hasNextPage = currentPage < totalPages;
          const hasPreviousPage = currentPage > 1;

          if (hasNextPage) {
            pendingPageAfterActionRef.current = currentPage + 1;
          } else if (hasPreviousPage) {
            pendingPageAfterActionRef.current = currentPage - 1;
          } else {
            pendingPageAfterActionRef.current = 1;
          }

          onMarkDuplicate?.(effectiveSelected);
          effectiveSetSelected([]);
        }}
        onDownloadPDF={handleDownloadPDF}
      />

      <div ref={tableRef}>
        <DuplicateTable
          title={title}
          columns={columns}
          data={pagedData}
          pageSize={pagedData.length}
          showCheckbox={showCheckbox}
          onTagPrimary={(row) => onTagPrimary?.(row)}
          selectedRows={effectiveSelected}
          onRowSelect={effectiveSetSelected}
          emptyComponent={emptyComponent}
          tableClassName={tableClassName}
        />
      </div>

      {/* Off-screen full-dataset render used only for PDF export so that every
          page's records are captured, not just the visible page. */}
      {isExporting && (
        <div
          ref={exportRef}
          className="pdf-export-root"
          style={{ position: 'absolute', top: 0, left: '-10000px', zIndex: -1 }}
          aria-hidden="true"
        >
          <div className="pdf-export-title">{displayTitle}</div>
          <DuplicateTable
            title={title}
            columns={columns}
            data={data}
            pageSize={data.length}
            onTagPrimary={noop}
            selectedRows={[]}
            onRowSelect={noop}
            emptyComponent={emptyComponent}
            tableClassName={tableClassName}
          />
        </div>
      )}

       {data.length > PAGE_SIZE && (
      <div className="global-pagination">
        <button
          className="nav-btn"
          disabled={currentPage === 1 || totalPages <= 1}
          onClick={() => handlePageChange(currentPage - 1)}
        >
          &lt; Back
        </button>
        <button
          className="nav-btn"
          disabled={currentPage >= totalPages || totalPages <= 1}
          onClick={() => handlePageChange(currentPage + 1)}
        >
          Next &gt;
        </button>
       </div>
       )}
    </>
  );
};

DuplicateSection.propTypes = {
  title: PropTypes.string.isRequired,
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  onTagPrimary: PropTypes.func,
  onMarkDuplicate: PropTypes.func,
  selectedRows: PropTypes.array,
  onRowSelect: PropTypes.func,
  actionLabel: PropTypes.string,
  showActions: PropTypes.bool,
  emptyComponent: PropTypes.node,
  showCheckbox: PropTypes.bool,
  tableClassName: PropTypes.string,
};

export default DuplicateSection;
